import { HttpError } from "@microbeta/common-frontend";
import type {
  CreateUserProfileRequest,
  UpdateUserProfileRequest,
  UserProfile,
} from "@microbeta/common-types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDebouncedValue } from "@/shared/hooks";
import { httpClient } from "@/shared/utils/httpClientInstance";
import config from "../../../config";
import { useAuthContext } from "../AuthContext";

interface HandleCheckResponse {
  available: boolean;
}

const BASE_URL = config.VITE_AUTH_BASE_URL;

// Query keys
export const userProfileKeys = {
  all: ["userProfile"] as const,
  profile: () => [...userProfileKeys.all, "profile"] as const,
  handleCheck: (handle: string) =>
    [...userProfileKeys.all, "handleCheck", handle] as const,
};

// Get current user profile
export const useUserProfile = () => {
  const { authState } = useAuthContext();

  return useQuery({
    queryKey: userProfileKeys.profile(),
    queryFn: async (): Promise<UserProfile> => {
      // Reduce timeout to 5 seconds to fail faster
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error("Profile fetch timeout")), 5000);
      });

      const requestPromise = (async () => {
        try {
          const response = await httpClient.get<UserProfile>(
            `${BASE_URL}/users/profile`,
            {
              headers: {
                Authorization: `Bearer ${authState?.idToken}`,
              },
            }
          );
          return response;
        } catch (error: unknown) {
          // Handle 503 errors (database not configured) gracefully
          if (error instanceof HttpError && error.status === 503) {
            console.warn("Database not configured for user profiles");
            throw new Error("Database not configured");
          }
          // Re-throw other errors (404 means no profile exists, which is expected)
          throw error;
        }
      })();

      // Race between the request and timeout
      return Promise.race([requestPromise, timeoutPromise]);
    },
    enabled: !!authState?.idToken,
    retry: (failureCount, error) => {
      // Don't retry if it's a known error we want to handle
      if (error instanceof Error) {
        if (
          error.message.includes("timeout") ||
          error.message.includes("not configured") ||
          (error instanceof HttpError && [404, 503].includes(error.status))
        ) {
          return false;
        }
      }
      // Only retry network errors, max 2 times
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff with max 30s
    staleTime: 60000, // Cache for 1 minute to reduce duplicate requests
    gcTime: 300000, // Keep in cache for 5 minutes (renamed from cacheTime in v5)
    throwOnError: false, // Don't throw errors, handle them gracefully
    refetchOnWindowFocus: false, // Prevent refetch on window focus to reduce requests
    refetchOnReconnect: false, // Prevent refetch on reconnect to reduce requests
    networkMode: "offlineFirst", // Allow stale data when offline
  });
};

// Check handle availability with debouncing
export const useCheckHandle = (handle: string) => {
  const { authState } = useAuthContext();
  const debouncedHandle = useDebouncedValue(handle, 500); // 500ms delay

  return useQuery({
    queryKey: userProfileKeys.handleCheck(debouncedHandle),
    queryFn: async (): Promise<HandleCheckResponse> => {
      // Reduce timeout to 5 seconds
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error("Request timeout")), 5000);
      });

      const requestPromise = (async () => {
        try {
          const response = await httpClient.post<HandleCheckResponse>(
            `${BASE_URL}/users/check-handle`,
            { handle: debouncedHandle },
            {
              headers: {
                Authorization: `Bearer ${authState?.idToken}`,
              },
            }
          );
          return response;
        } catch (error: unknown) {
          // A 404 means the handle doesn't exist, so it's available
          if (error instanceof HttpError && error.status === 404) {
            return { available: true };
          }
          // Handle 503 errors (database not configured) gracefully
          if (error instanceof HttpError && error.status === 503) {
            console.warn(
              "Database not configured, treating handle as available"
            );
            return { available: true };
          }
          // Re-throw other errors (401, 500, network errors, etc.)
          throw error;
        }
      })();

      // Race between the request and timeout
      return Promise.race([requestPromise, timeoutPromise]);
    },
    enabled:
      !!authState?.idToken && !!debouncedHandle && debouncedHandle.length >= 3,
    staleTime: 30000, // Cache for 30 seconds
    retry: (failureCount, error) => {
      // Don't retry timeout or known errors
      if (error instanceof Error && error.message.includes("timeout")) {
        return false;
      }
      return failureCount < 1; // Only retry once
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff with max 10s
    throwOnError: false, // Don't throw errors, handle them gracefully
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on reconnect
  });
};

// Create user profile
export const useCreateUserProfile = () => {
  const { authState } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      profileData: CreateUserProfileRequest
    ): Promise<UserProfile> => {
      const response = await httpClient.post<UserProfile>(
        `${BASE_URL}/users/profile`,
        profileData,
        {
          headers: {
            Authorization: `Bearer ${authState?.idToken}`,
          },
        }
      );
      return response;
    },
    onSuccess: (data) => {
      // Update the profile cache
      queryClient.setQueryData(userProfileKeys.profile(), data);
    },
  });
};

// Update user profile
export const useUpdateUserProfile = () => {
  const { authState } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      profileData: UpdateUserProfileRequest
    ): Promise<UserProfile> => {
      const response = await httpClient.put<UserProfile>(
        `${BASE_URL}/users/profile`,
        profileData,
        {
          headers: {
            Authorization: `Bearer ${authState?.idToken}`,
          },
        }
      );
      return response;
    },
    onSuccess: (data) => {
      // Update the profile cache
      queryClient.setQueryData(userProfileKeys.profile(), data);
    },
  });
};
